import { createEffect, onCleanup } from "solid-js";

interface UseScrollObserverOptions {
  containerAccessor: () => HTMLElement | null;
  setScrollTop: (val: number) => void;
  setContainerHeight: (val: number) => void;
}

export const useScrollObserver = (options: UseScrollObserverOptions) => {
  createEffect(() => {
    const el = options.containerAccessor();
    if (!el) {
      return;
    }
    // Coalesce scroll events to one signal update per animation frame. The
    // scroll event can fire many times per frame; without this the O(n)
    // virtualization memos recompute far more often than the screen can
    // repaint — which is what made scrolling feel heavier the longer the
    // (infinitely growing) list got.
    let rafHandle = 0;
    const handleScroll = () => {
      if (rafHandle) {
        return;
      }
      rafHandle = requestAnimationFrame(() => {
        rafHandle = 0;
        options.setScrollTop(el.scrollTop);
      });
    };
    const handleResize = () => {
      options.setContainerHeight(el.clientHeight);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    const ro = new ResizeObserver(handleResize);
    ro.observe(el);

    queueMicrotask(() => {
      handleResize();
      options.setScrollTop(el.scrollTop);
    });

    onCleanup(() => {
      if (rafHandle) {
        cancelAnimationFrame(rafHandle);
      }
      el.removeEventListener("scroll", handleScroll);
      ro.disconnect();
    });
  });
};
